import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./searchResults.css";
import HereMap from "../components/hereMap";
import { debounce } from "lodash"; // Ensure lodash is installed: npm install lodash
import FilterMenu from "./filterMenu"; // Import the FilterMenu component
import QuidproLogo from "../components/assets/quidproLowerLogo.png";
import Rating from "./assets/rating.png";
import Practice from "./assets/practice.png";
import LocationIcon from "./assets/location.png";
import FirmIcon from "./assets/firm.png";
import SearchHeader from "./searchHeader";
import ClientLoginPopup from "../../home/new/components/clientLoginPopup";
import Pagination from "@mui/material/Pagination";
import Footer from "../../components/footer";
import { getPracticeAreaLabel } from "../../helpers/stringHelper";

const SearchResultsClient = () => {
  const [isBottomReached, setIsBottomReached] = useState(false);
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const leftListRef = useRef(null);

  // Mobile-specific state
  const [activeTab, setActiveTab] = useState('list'); // 'list' or 'map'
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [mobileFilters, setMobileFilters] = useState({
    timeOfDay: '',
    practiceArea: '',
    language: '',
    experience: '',
    firmSize: '',
    radius: null
  });

  const footerRef = useRef(null);
  const getQueryParams = () => {
    return new URLSearchParams(location.search);
  };
  // Observe when the bottom of the left side is reached
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsBottomReached(true);
        } else {
          setIsBottomReached(false);
        }
      },
      {
        root: leftListRef.current, // The parent container of the left list
        threshold: 1.0, // Trigger when the entire bottom of the left side is visible
      }
    );

    const target = leftListRef.current;
    observer.observe(target);

    return () => observer.disconnect();
  }, []);

  // Scroll to footer when bottom is reached
  useEffect(() => {
    if (isBottomReached && footerRef.current) {
      window.scrollTo({
        top: footerRef.current.offsetTop,
        behavior: "smooth",
      });
    }
  }, [isBottomReached]);
  const [showLoginPopup, setShowLoginPopup] = useState(false);

  // Open Login Popup function
  const openLoginPopup = () => setShowLoginPopup(true);

  // Close Login Popup function
  const closeLoginPopup = () => setShowLoginPopup(false);
  const location = useLocation();
  const navigate = useNavigate();

  const splitSlotIntoHours = (slots) => {
    let totalHours = 0; // Variable to accumulate the total hours

    // Loop through each slot in the slots array
    slots.forEach((slot) => {
      // Parse the start and end times as Date objects
      let start = new Date(`1970-01-01T${slot.startTime}Z`);
      const end = new Date(`1970-01-01T${slot.endTime}Z`);

      // Calculate the difference in hours for the current slot
      const hoursAvailable = Math.floor((end - start) / (1000 * 60 * 60));

      // Add the calculated hours to the total
      totalHours += hoursAvailable;
    });

    return totalHours; // Return the total hours
  };

  // State for the lawyers list, current page, and total pages
  const [lawyers, setLawyers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0); // To track total items

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    when: undefined,
    date: undefined,
    timeOfDay: "",
    practiceArea: "",
    languageSpoken: "",
    experience: "",
    firmSize: "",
    radius: null,
  });

  useEffect(() => {
    console.log("Current filters:", filters);
  }, [filters]);

  // after your `const [filters, setFilters] = useState(...)`
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // only add keys that have values:
    if (filters.when) params.set("timeframe", filters.when);
    if (filters.date) params.set("date", filters.date.toISOString());
    if (filters.timeOfDay) params.set("time", filters.timeOfDay);
    if (filters.practiceArea) params.set("practice_area", filters.practiceArea);
    if (filters.languageSpoken) params.set("language", filters.languageSpoken);
    if (filters.experience) params.set("experience", filters.experience);
    if (filters.firmSize) params.set("firm_size", filters.firmSize);
    if (filters.radius != null) params.set("radius", filters.radius * 1600);

    // reset back to page=1 whenever filters change
    params.set("page", "1");

    // push new query string without reloading
    navigate({ search: params.toString() }, { replace: true });

    // reset your page state
    setCurrentPage(1);
  }, [
    filters.when,
    filters.date,
    filters.timeOfDay,
    filters.practiceArea,
    filters.languageSpoken,
    filters.experience,
    filters.firmSize,
    filters.radius,
    navigate,
    location.pathname,
    location.search,
  ]);

  const query = getQueryParams();
  const name = query.get("name") || "";
  const practice_area = query.get("practice_area") || "";
  const latitude = query.get("latitude");
  const longitude = query.get("longitude");
  const radius = query.get("radius"); // ❶ no default here!
  const loc = query.get("location");
  const timeframe = query.get("timeframe") || filters.when || "";
  const dateParam = query.get("date") || "";
  const timeOfDay = query.get("time") || filters.timeOfDay || "";
  const language = query.get("language") || filters.languageSpoken || "";
  const firm_size = query.get("firm_size") || filters.firmSize || "";
  const experience = query.get("experience") || filters.experience || "";

  /**
   * Helper function to parse query parameters
   */

  /**
   * Helper function to format date to YYYY-MM-DD
   */
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = `0${date.getMonth() + 1}`.slice(-2); // Months are zero-indexed
    const day = `0${date.getDate()}`.slice(-2);
    return `${year}-${month}-${day}`;
  };

  /**
   * fetchLawyers
   *
   * Retrieves lawyer data from the API using search parameters.
   * @param {number} page - The page number to fetch.
   */
  const fetchLawyers = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError(null);

      /* ---------- gather inputs ---------- */

      /* ---------- date range ---------- */
      const today = new Date();
      const startDate = formatDate(today);
      const endDate = formatDate(
        new Date(today.getTime() + 13 * 24 * 60 * 60 * 1000)
      );

      /* ---------- validate location ---------- */
      const lat = parseFloat(latitude);
      const lon = parseFloat(longitude);
      const rad = parseFloat(radius);

      const hasLocation = latitude && longitude && radius;
      const validLocation =
        !isNaN(lat) &&
        lat >= -90 &&
        lat <= 90 &&
        !isNaN(lon) &&
        lon >= -180 &&
        lon <= 180 &&
        !isNaN(rad) &&
        rad > 0 &&
        rad <= 40000; // ❷ match server cap

      if (hasLocation && !validLocation) {
        setError("Invalid location parameters.");
        setLoading(false);
        return;
      }

      /* ---------- build URL ---------- */
      const apiUrl = new URL(
        "/api/attorney/searchLawyersWithSchedules",
        window.location.origin
      );
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "10",
        name,
        practice_area,
        start_date: startDate,
        end_date: endDate,
      });

      if (validLocation) {
        // ❸ append only if valid
        params.set("latitude", lat.toString());
        params.set("longitude", lon.toString());
        params.set("radius", rad.toString());
      }

      if (timeframe) params.set("timeframe", timeframe);
      if (dateParam) params.set("date", dateParam);
      if (timeOfDay) params.set("time", timeOfDay);
      if (language) params.set("language", language);
      if (firm_size) params.set("firm_size", firm_size);
      if (experience) params.set("experience", experience);

      apiUrl.search = params.toString();
      console.log("▶️ Calling API:", apiUrl.toString());

      /* ---------- call API (unchanged) ---------- */
      try {
        const response = await fetchWithRetry(
          apiUrl.toString(),
          { method: "GET", headers: { "Content-Type": "application/json" } },
          3,
          500
        );
        if (!response.ok) {
          const { error } = await response.json();
          throw new Error(error || `HTTP ${response.status}`);
        }
        const data = await response.json();
        setLawyers(data.lawyers);
        setTotalPages(data.totalPages || 1);
        setCurrentPage(data.currentPage || page);
        setTotalItems(data.totalItems || data.lawyers.length);
      } catch (err) {
        console.error("Error fetching lawyers:", err);
        setError(
          err.message.includes("429")
            ? "Too many requests. Please try again later."
            : "Failed to fetch lawyers. Please try again."
        );
      } finally {
        setLoading(false);
      }
    },
    [filters, location.search]
  );

  /**
   * Exponential Backoff Fetch with Retry
   */
  const fetchWithRetry = async (url, options, retries = 3, backoff = 500) => {
    try {
      const response = await fetch(url, options);
      if (response.status === 429 && retries > 0) {
        await new Promise((resolve) => setTimeout(resolve, backoff));
        return fetchWithRetry(url, options, retries - 1, backoff * 2);
      }
      return response;
    } catch (error) {
      if (retries > 0) {
        await new Promise((resolve) => setTimeout(resolve, backoff));
        return fetchWithRetry(url, options, retries - 1, backoff * 2);
      }
      throw error;
    }
  };

  /**
   * Debounced Fetch to Prevent Rapid Calls
   */
  const debouncedFetchLawyers = useCallback(
    debounce((page) => {
      fetchLawyers(page);
    }, 300),
    [fetchLawyers]
  );

  /**
   * Effects
   *
   * 1. On mount (and page changes), fetch the lawyers for the current page.
   */
  useEffect(() => {
    debouncedFetchLawyers(currentPage);
    return debouncedFetchLawyers.cancel;
  }, [currentPage, debouncedFetchLawyers]);

  /**
   * handlePageChange
   *
   * Called when a user clicks a pagination button.
   * @param {number} pageNumber
   */
  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return; // Prevent invalid page numbers
    setCurrentPage(pageNumber);
    // Optionally, scroll to top or maintain scroll position
  };

  const handleLogoClick = () => {
    navigate("/");
  };

  // Mobile helper functions
  const handleMobileTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleMobileFilterChange = (filterType, value) => {
    setMobileFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const applyMobileFilters = () => {
    setFilters(prev => ({
      ...prev,
      timeOfDay: mobileFilters.timeOfDay,
      practiceArea: mobileFilters.practiceArea,
      languageSpoken: mobileFilters.language,
      experience: mobileFilters.experience,
      firmSize: mobileFilters.firmSize,
      radius: mobileFilters.radius
    }));
    setShowMobileFilters(false);
  };

  const clearMobileFilters = () => {
    setMobileFilters({
      timeOfDay: '',
      practiceArea: '',
      language: '',
      experience: '',
      firmSize: '',
      radius: null
    });
  };

  const getActiveFilterCount = () => {
    return Object.values(mobileFilters).filter(value => 
      value !== '' && value !== null && value !== undefined
    ).length;
  };

  // Construct array of page numbers (1..totalPages)

  return (
    <div className="lawyers-page-container">
      <SearchHeader
        openLoginPopup={openLoginPopup}
        headerColor="search-header-gray"
        initialName={name}
        initialPracticeArea={practice_area}
        initialLatitude={latitude}
        initialLongitude={longitude}
        initialRadius={radius}
        initialLocation={loc}
      />

      {/* Mobile Tab Navigation */}
      <div className="mobile-tab-nav">
        <button 
          className={`mobile-tab-btn ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => handleMobileTabChange('list')}
        >
          List
        </button>
        <button 
          className={`mobile-tab-btn ${activeTab === 'map' ? 'active' : ''}`}
          onClick={() => handleMobileTabChange('map')}
        >
          Map
        </button>
      </div>

      {/* Mobile Filter System */}
      <div className="mobile-filter-container">
        <button 
          className="mobile-filter-toggle"
          onClick={() => setShowMobileFilters(true)}
        >
          Filters
          {getActiveFilterCount() > 0 && (
            <span className="filter-count">{getActiveFilterCount()}</span>
          )}
        </button>
      </div>

      {/* Mobile Filters Drawer */}
      <div className={`mobile-filters-drawer ${showMobileFilters ? 'open' : ''}`}>
        <div className="mobile-filters-content">
          <div className="mobile-filters-header">
            <h3 className="mobile-filters-title">Filters</h3>
            <button 
              className="mobile-filters-close"
              onClick={() => setShowMobileFilters(false)}
            >
              ×
            </button>
          </div>

          <div className="mobile-filter-group">
            <div className="mobile-filter-group-title">Time of Day</div>
            <div className="mobile-filter-options">
              {['Morning', 'Afternoon', 'Evening'].map(time => (
                <button
                  key={time}
                  className={`mobile-filter-option ${mobileFilters.timeOfDay === time.toLowerCase() ? 'selected' : ''}`}
                  onClick={() => handleMobileFilterChange('timeOfDay', time.toLowerCase())}
                >
                  {time}
                </button>
              ))}
            </div>
          </div>

          <div className="mobile-filter-group">
            <div className="mobile-filter-group-title">Practice Area</div>
            <div className="mobile-filter-options">
              {['Family Law', 'Criminal Defense', 'Personal Injury', 'Business Law', 'Real Estate'].map(area => (
                <button
                  key={area}
                  className={`mobile-filter-option ${mobileFilters.practiceArea === area.toLowerCase() ? 'selected' : ''}`}
                  onClick={() => handleMobileFilterChange('practiceArea', area.toLowerCase())}
                >
                  {area}
                </button>
              ))}
            </div>
          </div>

          <div className="mobile-filter-group">
            <div className="mobile-filter-group-title">Experience</div>
            <div className="mobile-filter-options">
              {['1-5 years', '5-10 years', '10+ years'].map(exp => (
                <button
                  key={exp}
                  className={`mobile-filter-option ${mobileFilters.experience === exp ? 'selected' : ''}`}
                  onClick={() => handleMobileFilterChange('experience', exp)}
                >
                  {exp}
                </button>
              ))}
            </div>
          </div>

          <div className="mobile-filter-actions">
            <button 
              className="mobile-filter-btn mobile-filter-clear"
              onClick={clearMobileFilters}
            >
              Clear All
            </button>
            <button 
              className="mobile-filter-btn mobile-filter-apply"
              onClick={applyMobileFilters}
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      <header className="header-filters">
        <FilterMenu filters={filters} onFilterChange={setFilters} />
      </header>

      {/* -----------------------------------
          2) MAIN CONTENT: Lawyer List + Map
         ----------------------------------- */}
      <div className="search-main-content">
        {/* LEFT SIDE: Lawyer Cards / Listing */}
        {(!isMapExpanded && activeTab === 'list') && (
          <div className="left-list" ref={leftListRef}>
          <div className="results-count">
            {/* Display total providers found. Could also be from the server. */}
            <strong>{totalItems} providers</strong>
          </div>

          {/* Loading and Error States */}
          {loading && <div className="spinner"></div>}
          {error && <div className="error-message">{error}</div>}
          {!loading && !error && lawyers.length === 0 && (
            <div>No lawyers found for your search criteria.</div>
          )}

          {/* Lawyer Cards */}
          {!loading &&
            !error &&
            lawyers.map((lawyer) => (
              <div
                className="lawyer-card"
                key={lawyer.user_id}
                onClick={() =>
                  navigate(`/lawyer-detail/${lawyer.user_id}`, {
                    state: { lawyer },
                  })
                }
                style={{ cursor: "pointer" }}
              >
                {/* Wrapper for Lawyer Info and Availability */}
                <div className="lawyer-details">
                  {/* Lawyer Basic Info */}
                  <div className="lawyer-info">
                    {/* Lawyer Header */}
                    <div className="lawyer-header-search">
                      {lawyer.profile_photo_url ? (
                        <img
                          src={lawyer.profile_photo_url}
                          alt={`${lawyer.name}'s profile`}
                          className="profile-photo"
                          loading="lazy"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/default-avatar.png"; // Path to a default avatar image
                          }}
                        />
                      ) : (
                        <div className="profile-photo placeholder">
                          {/* Placeholder initials or icon can be added here */}
                          {lawyer.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="name-rating">
                        <div className="lawyer-name">{lawyer.name}</div>
                        <div className="info-item">
                          <img
                            src={Rating}
                            alt="Rating"
                            className="info-icon"
                            style={{ width: "16px", height: "16px" }}
                          />
                          <span className="rating">
                            {lawyer.average_rating}
                          </span>
                          <span className="reviews">
                            ({lawyer.number_of_reviews} reviews)
                          </span>
                        </div>
                        <div className="info-item">
                          <img
                            src={FirmIcon}
                            alt="Company"
                            className="info-icon"
                          />
                          <span className="company-name">
                            {lawyer.firm_name}
                          </span>
                        </div>
                        <div className="info-item">
                          <img
                            src={LocationIcon}
                            alt="Address"
                            className="info-icon"
                          />
                          <span className="lawyer-address">
                            {lawyer.city}, {lawyer.state} {lawyer.zip_code}
                          </span>
                        </div>
                        <div className="info-item">
                          <img
                            src={Practice}
                            alt="Practice Areas"
                            className="info-icon"
                          />
                          <span className="practice-areas">
                            {lawyer.practice_areas
                              .map((area) => getPracticeAreaLabel(area))
                              .join(", ")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {lawyer.has_schedule &&
                  lawyer.availability &&
                  lawyer.availability.length > 0 ? (
                    <div className="availability-grid">
                      {lawyer.availability.map((dayObj) => (
                        <div
                          className={`day-slot ${
                            dayObj.slots.length > 0 ? "has-slots" : "zero-slots"
                          }`}
                          key={dayObj.date}
                        >
                          {/* Check if slots exist and render available hours */}
                          {dayObj.slots && dayObj.slots.length > 0 ? (
                            <div className="slots">
                              <div className="day-label">{dayObj.day}</div>
                              <div className="date-label">{dayObj.date}</div>
                              {/* Pass the array of slots to the splitSlotIntoHours function */}
                              <p className="slot-number-text">
                                {splitSlotIntoHours(dayObj.slots)}{" "}
                              </p>

                              <p className="slot-available-text">
                                {" "}
                                Available Slot
                                {splitSlotIntoHours(dayObj.slots) !== 1
                                  ? "s"
                                  : ""}
                              </p>
                            </div>
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                              }}
                            >
                              <div>
                                <div className="day-label-not-available">
                                  {dayObj.day}
                                </div>
                                <div className="date-label-not-available">
                                  {dayObj.date}
                                </div>
                              </div>
                              <div className="not-available">Not Available</div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "60%",
                      }}
                    >
                      <div className="not-available-banner">
                        <p>Not Available</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* "View all availability" link */}
                <div className="view-all">
                  <a href="#!">View all availability</a>
                </div>

                {/* Mobile Action Buttons */}
                <div className="mobile-lawyer-actions">
                  <button 
                    className="mobile-book-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Handle booking logic here
                      console.log('Book appointment for:', lawyer.name);
                    }}
                  >
                    Book Appointment
                  </button>
                  <button 
                    className="mobile-view-details-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/lawyer-detail/${lawyer.user_id}`, {
                        state: { lawyer },
                      });
                    }}
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}

          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(event, value) => handlePageChange(value)}
              siblingCount={2}
              boundaryCount={1}
              shape="rounded"
              disabled={loading}
              sx={{
                "& .MuiPaginationItem-root": {
                  width: "35px", // Adjust width for circular style
                  height: "35px", // Adjust height for circular style
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "transparent", // Transparent background for pagination items
                  "&:hover": {
                    backgroundColor: "#F3F3F3", // Light gray background on hover
                  },
                },
                "& .Mui-selected": {
                  backgroundColor: "#F3F3F3 !important", // Active page background color
                  color: "black", // Active page text color
                },
                "& .MuiPaginationItem-previousNext": {
                  backgroundColor: "transparent",
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                },
              }}
            />
          )}
          </div>
        )}

        {/* RIGHT SIDE: Map (HereMap component) */}
        <div className={`right-map ${isMapExpanded ? "expanded" : ""} ${activeTab === 'map' ? 'active' : ''}`} style={isMapExpanded ? { position: "fixed", top: "157px", left: 0, right: 0, bottom: 0, zIndex: 1000, background: "#fff" } : {}}>
          {/* Top-left control to collapse when expanded */}
          {isMapExpanded && (
            <button
              aria-label="Show list"
              onClick={() => setIsMapExpanded(false)}
              style={{
                position: "fixed",
                top: 200,
                right: 35,
                zIndex: 1100,
                background: "#fff",
                border: "1px solid #ddd",
                borderRadius: 10,
                padding: "10px 14px",
                cursor: "pointer",
                boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
              }}
            >
              Show list
            </button>
          )}

          {/* Conditionally render HereMap only when lawyers data is available */}
          {!loading && !error && lawyers.length > 0 && (
            <HereMap lawyers={lawyers} isExpanded={isMapExpanded} />
          )}
          {/* Optionally, show a placeholder or message when the map isn't rendered */}
          {(loading || error || lawyers.length === 0) && (
            <div
              style={{
                width: "100%",
                height: isMapExpanded ? "100vh" : "500px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f0f0f0",
                borderRadius: isMapExpanded ? 0 : 8,
                color: "#555",
                fontSize: "18px",
              }}
            >
              {loading && "Loading map..."}
              {error && "Map cannot be displayed due to an error."}
              {!loading && !error && lawyers.length === 0 && "Map is unavailable."}
            </div>
          )}

          {/* Bottom-left expand button when not expanded */}
          {!isMapExpanded && (
            <button
              aria-label="Expand map"
              onClick={() => setIsMapExpanded(true)}
              style={{
                position: "absolute",
                top: 200,
                right: 35,
                zIndex: 1100,
                background: "#fff",
                border: "1px solid #ddd",
                borderRadius: 10,
                padding: "10px 14px",
                cursor: "pointer",
                boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
              }}
            >
              Expand map
            </button>
          )}
        </div>
        {showLoginPopup && (
          <ClientLoginPopup show={showLoginPopup} onClose={closeLoginPopup} />
        )}
      </div>

      {/* Mobile Map Toggle Button */}
      <div className="mobile-map-controls">
        <button 
          className="mobile-map-toggle"
          onClick={() => setActiveTab(activeTab === 'map' ? 'list' : 'map')}
        >
          {activeTab === 'map' ? '📋' : '🗺️'}
        </button>
      </div>

      {!isMapExpanded && (
        <div ref={footerRef}>
        <Footer />
        </div>
      )}
    </div>
  );
};

export default SearchResultsClient;
